// Importing useState and useEffect hooks from React for managing authentication state and side effects
import { useState, useEffect } from 'react';
// Importing a pre-configured Axios instance for making authenticated HTTP requests to the backend
import axiosClient from '../api/axiosClient';
// Importing the User type for strong typing of the user object
import type { User } from '../types';
// Defining the shape of authentication state and functions returned by useAuth hook

interface AuthState {
  // The currently authenticated user or null if no user is logged in
  user: User | null;
  // Boolean indicating if authentication data is still being loaded
  isLoading: boolean;
  // Function to log in a user using credentials
  login: (username: string, password: string) => Promise<void>;
  // Function to log out the currently authenticated user
  logout: () => Promise<void>;
  // Function to refresh the currently logged-in user’s data from backend
  refreshUser: () => Promise<void>;
}
// Helper function to normalize user data received from backend responses
const normalizeUser = (maybeUser: any): User | null => {
  if (!maybeUser) {
    return null; // No user data
  }
  if (typeof maybeUser === 'string') {
    return null; // Reject HTML responses (e.g., Django CSRF errors)
  }
  if (maybeUser.user && typeof maybeUser.user === 'object') {
    return maybeUser.user as User; // Handle case where user is nested
  }
  if (typeof maybeUser === 'object') {
    return maybeUser as User; // Direct user object returned
  }
  return null; // Fallback for invalid data
};
// Helper function to extract a cookie value by name
const getCookie = (name: string): string | null => {
  const value = `; ${document.cookie}`; // Prefix with semicolon for parsing
  const parts = value.split(`; ${name}=`); // Split at cookie name
  if (parts.length === 2) return parts.pop()?.split(';').shift() || null; // Return cookie value if found
  return null; // Return null if not found
};
// Custom React hook to manage authentication state and actions
export const useAuth = (): AuthState => {
  // State for currently authenticated user
  const [user, setUser] = useState<User | null>(null);
  // State for loading indicator (e.g., when fetching user data or logging in)
  const [isLoading, setIsLoading] = useState(true);
  // Function to fetch the current user from backend
  const fetchUser = async () => {
    try {
      const response = await axiosClient.get('/elearning/api/get_user/');
      const normalized = normalizeUser(response.data);
      setUser(normalized); // Update user state
    } catch (err: any) {
      // Log error details and clear user state on failure
      console.error('[useAuth] fetchUser error:', {
        message: err.message,
        response: err.response?.data,
        status: err.response?.status,
      });
      setUser(null);
    } finally {
      setIsLoading(false); // Stop loading state regardless of success/failure
    }
  };
  // useEffect runs on mount to check session cookie and fetch user if valid
  useEffect(() => {
    const sessionId = getCookie('sessionid');
    if (sessionId) {
      fetchUser();
    } else {
      setIsLoading(false); // Skip fetching if no session exists
    }
  }, []);
  // Function to handle login process
  const login = async (username: string, password: string) => {
    setIsLoading(true); // Begin loading
    try {
      // Fetch CSRF token required by Django backend
     await axiosClient.get('/elearning/get-csrf/');
      // Attempt login with provided credentials
      const response = await axiosClient.post('/elearning/api/login/', { username, password });
      const normalized = normalizeUser(response.data);
      setUser(normalized); // Update user state
    } catch (err: any) {
      // Log detailed error info and reset user state
      console.error('[useAuth] Login error:', {
        message: err.message,
        response: err.response?.data,
        status: err.response?.status,
      });
      setUser(null);
      throw new Error(err.response?.data?.message || 'Login failed'); // Propagate error
    } finally {
      setIsLoading(false); // Stop loading
    }
  };
  // Function to handle logout process
  const logout = async () => {
    try {
      await axiosClient.post('/elearning/api/logout/', {}); // Send logout request
      setUser(null); // Clear user state
    } catch (err: any) {
      // Log error but keep state consistent
      console.error('[useAuth] Logout error:', {
        message: err.message,
        response: err.response?.data,
        status: err.response?.status,
      });
    }
  };
  // Function to refresh user data (e.g., re-fetch from backend)
  const refreshUser = async () => {
    setIsLoading(true);
    await fetchUser();
  };
  // Returning the authentication state and actions for use in components
  return { user, isLoading, login, logout, refreshUser };
};
